import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useIdentity } from '../../identity/state/useIdentity';
import { payrollReviewGateway } from '../../payroll-review';
import type { PayrollReviewRun } from '../../payroll-review/types';
import { Unavailable } from '../components/Unavailable';
import { CLIENT_WORKSPACE_SECTIONS } from '../partnerNav';
import { partnerErrorMessage } from '../state/partnerError';
import { useClientSession } from '../state/useClientSession';
import { useLoad } from '../state/useLoad';
import { usePartner } from '../state/usePartner';

/** What each delegated scope means to the person, in words. The scope list itself is the server's; this is only its explanation. */
const SCOPE_WORDS: Record<string, string> = {
  'payroll.read': 'View payroll runs and payroll reports',
  'payroll.prepare': 'Prepare draft payrolls and submit them to the client for approval',
  'tax.read': 'View tax liabilities, due dates and filing status',
  'tax.setup.prepare': 'Prepare tax setup for the client to review',
  'tax.filing.prepare': 'Prepare draft tax forms',
  'company.setup': 'Set up the company profile, work locations and pay schedules',
  'employees.setup': 'Set up employees and their pay rates',
  'history.import': 'Prepare a payroll history import',
  'benefits.setup': 'Set up benefits, deductions and earnings',
  'accounting.export': 'Create accounting exports',
};

/** The client workspace sections that can open today. Everything else is listed as not available, because most routes are still closed to a partner (they are opened one by one, after review). */
const OPEN_SECTIONS = new Set<string>(['Client Overview', 'Payroll Review']);

/**
 * Inside a client company. Today the only data a partner can read here is the client's payroll runs (read only); every other section is listed as not available. There is nothing to edit, so nothing can be lost when
 * the 10-minute session ends: when write routes are opened, each form must keep its work on the server (as a draft) before it is offered.
 */
export function ClientWorkspacePage() {
  const { relationshipId = '' } = useParams();
  const { membership } = usePartner();
  const { status, delegation, enter } = useClientSession();
  const { me } = useIdentity();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status !== 'active' || delegation?.relationshipId !== relationshipId) {
    return (
      <>
        <h1>Client workspace</h1>
        <section className="pp-card">
          <p>You are not working inside this client right now.</p>
          {status === 'none' && (
            <>
              <button
                type="button"
                className="pp-button pp-button--primary"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await enter(membership.partnerOrganizationId, relationshipId);
                  } catch (e) {
                    setError(partnerErrorMessage(e, 'You cannot open this client.'));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Open this client
              </button>
              {error && <p role="alert" className="pp-error">{error}</p>}
            </>
          )}
          <p><Link to={`/partner/clients/${relationshipId}`}>Back to the client</Link></p>
        </section>
      </>
    );
  }

  const canReadPayroll = me?.activePermissions?.includes('payrollruns.read') ?? false;

  return (
    <>
      <h1>{delegation.clientName}</h1>
      <section className="pp-card" aria-labelledby="pp-scope-h">
        <h2 id="pp-scope-h">What you can do for this client</h2>
        <ul className="pp-list">
          {delegation.scopes.map((scope) => <li key={scope}>{SCOPE_WORDS[scope] ?? scope}</li>)}
        </ul>
        <p className="pp-muted">Most of this is not open in the portal yet. Routes are opened one at a time, after review, so what you can actually use is shown below.</p>
      </section>

      <section className="pp-card" aria-labelledby="pp-sections-h">
        <h2 id="pp-sections-h">Sections</h2>
        <ul className="pp-chips">
          {CLIENT_WORKSPACE_SECTIONS.map((section) => {
            const open = OPEN_SECTIONS.has(section) && (section !== 'Payroll Review' || canReadPayroll);
            return (
              <li key={section} className={open ? 'pp-chip' : 'pp-chip pp-chip--off'} aria-disabled={open ? undefined : true}>
                {section}{section === 'Payroll Review' && open ? ' (read only)' : open ? '' : ' (not available yet)'}
              </li>
            );
          })}
        </ul>
      </section>

      {canReadPayroll ? <PayrollRunsReadOnly clientKey={relationshipId} /> : <Unavailable>Your access to this client does not include viewing payroll runs.</Unavailable>}
    </>
  );
}

function PayrollRunsReadOnly({ clientKey }: { clientKey: string }) {
  const runs = useLoad((signal) => payrollReviewGateway.listRuns(signal), clientKey, 'The payroll runs could not be loaded.');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const detail = useLoad(
    (signal) => (selectedId ? payrollReviewGateway.getRun(selectedId, signal) : Promise.resolve(null)),
    `${clientKey}:${selectedId ?? ''}`,
    'That payroll run could not be loaded.',
  );
  const run: PayrollReviewRun | null = detail.data;

  return (
    <section className="pp-card" aria-labelledby="pp-runs-h">
      <h2 id="pp-runs-h">Payroll runs (read only)</h2>
      <p className="pp-muted">You can look at these. Approving, changing or paying a payroll is not something a partner can do here.</p>
      {runs.error && <p role="alert" className="pp-error">{runs.error}</p>}
      {runs.loading && !runs.data && <p className="pp-muted">Loading…</p>}
      {runs.data && runs.data.length === 0 && <p className="pp-muted">This client has no payroll runs.</p>}
      {runs.data && runs.data.length > 0 && (
        <div className="pp-table-wrap">
          <table className="pp-table">
            <caption className="pp-sr">Payroll runs for this client</caption>
            <thead>
              <tr><th scope="col">Pay period</th><th scope="col">Check date</th><th scope="col">Status</th><th scope="col"><span className="pp-sr">Details</span></th></tr>
            </thead>
            <tbody>
              {runs.data.map((r) => (
                <tr key={r.id}>
                  <th scope="row">{r.periodStartDate} to {r.periodEndDate}</th>
                  <td>{r.checkDate}</td>
                  <td>{r.status}</td>
                  <td><button type="button" className="pp-link-button" aria-expanded={selectedId === r.id} onClick={() => setSelectedId(selectedId === r.id ? null : r.id)}>{selectedId === r.id ? 'Hide' : 'View'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && (
        <div className="pp-card" role="region" aria-label="Payroll run details">
          {detail.error && <p role="alert" className="pp-error">{detail.error}</p>}
          {run && (
            <dl className="pp-facts">
              <div><dt>Status</dt><dd>{run.status}</dd></div>
              <div><dt>Pay period</dt><dd>{run.periodStartDate} to {run.periodEndDate}</dd></div>
              <div><dt>Check date</dt><dd>{run.checkDate}</dd></div>
              <div><dt>Prepared by</dt><dd>{run.createdByEmailSnapshot}</dd></div>
              <div><dt>Submitted for approval</dt><dd>{run.submittedByEmailSnapshot ? `${run.submittedByEmailSnapshot} · ${new Date(run.submittedForApprovalAtUtc ?? '').toLocaleString()}` : 'Not yet'}</dd></div>
              <div><dt>Approved</dt><dd>{run.approvedByEmailSnapshot ? `${run.approvedByEmailSnapshot} · ${new Date(run.approvedAtUtc ?? '').toLocaleString()}` : 'Not yet'}</dd></div>
              {run.rejectionReason && <div><dt>Rejected because</dt><dd>{run.rejectionReason}</dd></div>}
            </dl>
          )}
        </div>
      )}
    </section>
  );
}
